import { observable, action, computed, reaction, makeObservable } from 'mobx';
import { debounce } from 'debounce';
import {
  ViewMode,
  DrawLineStringMode,
  DrawPointMode,
  DrawPolygonMode,
  ModifyMode,
  RotateMode,
  ElevationMode,
  TranslateMode
} from 'nebula.gl';
import ProjectStore from './ProjectStore';
import UserStore from './UserStore';
import ProjectDetailStore from './ProjectDetailStore';
import agent from '../lib/agent';

const INITIAL_VIEW_STATE = {
  latitude: 41.8825763,
  longitude: -71.3629619,
  zoom: 17,
  maxZoom: 18,
  pitch: 50,
  bearing: 0
};

const MODES = [
  {
    id: 'drawPolyline',
    text: 'Draw Polyline',
    handler: new DrawLineStringMode()
  },
  { id: 'drawPolygon', text: 'Draw Polygon', handler: new DrawPolygonMode() },
  { id: 'drawBuilding', text: 'Draw Building', handler: new DrawPolygonMode() },
  { id: 'drawText', text: 'Add Text', handler: new DrawPointMode() },
  { id: 'drawPoint', text: 'Draw Polygon', handler: new DrawPointMode() },
  { id: 'view', text: 'Edit Feature', handler: new ViewMode() },
  { id: 'rotate', text: 'Rotate', handler: new RotateMode() },
  { id: 'elevation', text: 'Elevation', handler: new ElevationMode() },
  { id: 'translate', text: 'Translate', handler: new TranslateMode() },
  { id: 'modify', text: 'Edit Feature', handler: new ModifyMode() }
];

class EditorStore {
  viewState = INITIAL_VIEW_STATE;
  isLoadingFeatures = false;
  pickedResults = new Set([]);
  resultMap = new Map();
  currentDataset = null;
  deletedFeatures = [];
  currentLayer = null;
  datasets = [];
  editMode = MODES.find(mode => mode.id === 'view');
  features = [];
  modal = false;
  mapBounds = null;
  layers = [];
  arcGISLayers = [];
  show3dTiles = false;

  searchResults = [];
  searchTerm = null;

  getFeature = featureId => {
    return this.features.find(feature => feature.id === featureId);
  };

  setSearchTerm = term => {
    this.searchTerm = term;
  };

  setSearchResults = results => {
    this.searchResults = results;
  };

  setViewState = viewState => {
    this.viewState = viewState;
  };

  search = async () => {
    // TODO remove groupId
    const groupId = UserStore.user?.group.id;
    const response = await agent.Dataset.search(this.searchTerm, groupId);
    this.setSearchResults(response.data.data);
  };

  reset = () => {
    this.currentDataset = null;
    this.editMode = MODES.find(mode => mode.id === 'view');
    this.features = [];
    this.modal = false;
  };

  deleteFeature = async id => {
    this.deletedFeatures = [...this.deletedFeatures, id];
    agent.Dataset.deleteFeature(this.currentDataset.id, id);
    const resultMap = new Map(this.resultMap);
    resultMap.delete(id);
    this.resultMap = resultMap;
    this.features = this.features.filter(feature => feature.id !== id);
    // ProjectDetailStore.removeBuilding(id.replace('pb_', ''));
  };

  getLayers = async () => {
    if (!this.currentDataset?.id) return;
    const response = await agent.Dataset.getLayers(this.currentDataset.id);

    this.layers = response.data.data;
  };

  constructor() {
    makeObservable(this, {
      show3dTiles: observable,
      viewState: observable,
      isLoadingFeatures: observable,
      pickedResults: observable,
      resultMap: observable,
      currentDataset: observable,
      arcGISLayers: observable,
      deletedFeatures: observable,
      currentLayer: observable,
      datasets: observable,
      editMode: observable,
      features: observable,
      modal: observable,
      mapBounds: observable,
      layers: observable,
      searchResults: observable,
      searchTerm: observable,
      getFeature: action,
      setSearchTerm: action,
      setSearchResults: action,
      setViewState: action,
      search: action,
      reset: action,
      deleteFeature: action,
      getLayers: action,
      addFeature: action,
      deleteAll: action,
      setFeatures: action,
      updateLayer: action,
      updateDataset: action,
      featureCollection: computed,
      projectId: computed,
      datasetId: computed,
      getDatasetFeatures: action,
      getLayerFeatures: action,
      setCurrentLayer: action,
      setModal: action,
      setMapBounds: action,
      updateFeature: action,
      createFeature: action,
      getDatasets: action,
      createDataset: action,
      createLayer: action,
      deleteDataset: action,
      deleteLayer: action,
      setCurrentDataset: action,
      setCurrentDatasetLayers: action,
      setCurrentDatasetName: action,
      getCurrentDataset: action,
      setSubdomains: action,
      setEditMode: action,
      filterResults: action
    });
  }

  addFeature(feature) {
    this.features.push(feature);
  }

  deleteAll = () => {
    // this.features.clear();
  };

  setFeatures = features => {
    this.features = features;
  };

  updateLayer = async (datasetId, layerId, data) => {
    const newLayers = [...this.layers];
    const index = newLayers.findIndex(layer => layer.id === layerId);
    newLayers[index] = {
      ...newLayers[index],
      ...data
    };

    this.layers = newLayers;
    await agent.Dataset.updateLayer(datasetId, layerId, data);
  };

  updateDataset = async (datasetId, data) => {
    await agent.Dataset.updateDataset(datasetId, data);
  };

  get featureCollection() {
    return {
      type: 'FeatureCollection',
      features: [...this.features]
    };
  }

  get projectId() {
    if (this.datasetId?.startsWith('pr_')) {
      return this.currentDataset?.id?.replace('pr_', '');
    }
    return null;
  }

  get datasetId() {
    return this.currentDataset?.id;
  }

  getDatasetFeatures = async () => {
    if (!this.currentDataset) return;
    const response = await agent.Dataset.getFeatures(this.currentDataset.id);
    this.features = [];
    this.resultMap = new Map();

    this.setFeatures(response.data.data);
    this.filterResults({
      layerId: this.currentLayer?.id
    });

    return response.data.data;
  };

  getLayerFeatures = async layerId => {
    const response = await agent.Dataset.getFeatures(
      this.currentDataset.id,
      layerId
    );
    this.features = response.data.data;
    return response.data.data;
  };

  setCurrentLayer = async (datasetId, layerId) => {
    if (this.currentLayer?.id === layerId) return;

    const response = await agent.Dataset.getLayer(datasetId, layerId);
    if (this.currentDataset?.id !== response.data.data.dataset.id) {
      this.currentDataset = response.data.data.dataset;
    }

    this.currentLayer = response.data.data;
    return this.currentLayer;
  };

  setModal = modal => {
    this.modal = modal;
  };

  setMapBounds = bounds => {
    this.mapBounds = bounds;
  };

  updateFeature = async feature => {
    // axios.post(`/dataset/${datasetId}/${feature.id}`, feature);
    agent.Dataset.updateFeature(this.datasetId, feature);
  };

  setArcGISLayers = layers => {
    this.arcGISLayers = layers;
  };

  createFeature = async feature => {
    const { datasetId } = this;
    if (datasetId === 'create_project') {
      return;
    }

    if (this.currentDataset.type === 'project') {
      const data = await ProjectStore.createProjectBuilding(datasetId, feature);
      ProjectDetailStore.addBuilding(data);
      return;
    }

    agent.Dataset.createFeature(datasetId, feature);
  };

  getDatasets = async () => {
    const { data } = await agent.Dataset.get();
    this.datasets = data.data;
  };

  createDataset = async options => {
    const response = await agent.Dataset.createDataset(options);
    this.getDatasets();
    return response.data.data;
  };

  createLayer = async data => {
    await agent.Dataset.createLayer(this.currentDataset.id, data);
    this.getLayers(this.currentDataset.id);
  };

  deleteDataset = async datasetId => {
    this.datasets = this.datasets.filter(dataset => dataset.id !== datasetId);
    await agent.Dataset.deleteDataset(datasetId);
  };

  deleteLayer = async (datasetId, layerId) => {
    const newLayers = [...this.layers].filter(layer => layer.id !== layerId);
    this.layers = newLayers;
    await agent.Dataset.deleteLayer(datasetId, layerId);
  };

  setCurrentDataset = async dataset => {
    // console.log('setCurrentDataset', dataset);
    if (typeof dataset === 'string') {
      if (dataset === this.currentDataset?.id) {
        return;
      }
      //   console.log('setCurrentDataset1');
      const response = await agent.Dataset.getDataset(dataset);
      //   console.log('setCurrentDataset2', response.data.data);
      this.currentDataset = response.data.data;
      return;
    }
    if (this.currentDataset?.id !== dataset.id) {
      this.deleteAll();
    }

    this.currentDataset = dataset;
  };

  setCurrentDatasetLayers = datasetIds => {
    this.currentDataset = {
      ...this.currentDataset,
      datasets: datasetIds
    };
  };

  setCurrentDatasetName = async name => {
    this.currentDataset = {
      ...this.currentDataset,
      name
    };

    const newDatasets = [...this.datasets];
    const index = newDatasets.findIndex(
      dataset => dataset.id === this.currentDataset.id
    );
    if (index > -1) newDatasets[index].name = name;
    this.datasets = newDatasets;

    await agent.Dataset.updateDataset(this.currentDataset.id, {
      name
    });
  };

  getCurrentDataset = () => {
    return this.datasets.find(d => d.current === true);
  };

  setSubdomains = subdomains => {
    this.currentDataset.subdomains = subdomains;
  };

  setEditMode = modeId => {
    const mode = MODES.find(mode => mode.id === modeId);
    if (!mode) return MODES.find(mode => mode.id === 'view');
    this.editMode = mode;
  };

  filterResults = ({ layerId }) => {
    const arr = this.features
      .filter(feature => {
        // filter out additional datasets
        if (feature.dataset_id !== this.datasetId) return false;
        if (layerId === 'uncategorized' || layerId === null)
          return (
            feature.layer_id === null || feature.layer_id === 'uncategorized'
          );
        return feature.layer_id === layerId;
      })
      .map(feature => {
        return [feature.id, feature];
      });

    this.resultMap = new Map(arr);
  };

  setShow3dTiles = show3dTiles => {
    this.show3dTiles = show3dTiles;
  };
}

const store = new EditorStore();

reaction(
  () => JSON.stringify([store.searchTerm]),
  debounce(() => {
    if (store.searchTerm) store.search();
    else store.setSearchResults([]);
  }, 200)
);

reaction(
  () => JSON.stringify([store.currentDataset]),
  () => {
    if (store.currentDataset?.id) store.getLayers(store.currentDataset?.id);
  }
);

reaction(
  () => store.datasetId,
  () => {
    if (store.datasetId && store.datasetId !== 'create_project')
      store.getDatasetFeatures();
  }
);

reaction(
  () => JSON.stringify(store.currentLayer?.id),
  () => {
    store.filterResults({
      layerId: store.currentLayer?.id
    });
  }
);

export default store;
